'use client';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useAccount } from '@/frontastic';
import { useShowActiveBrandSection } from '@/frontastic/hooks/useShowActiveBrandSection';
import { CustomerNotesPayload } from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import ViewAllNotesModals from '../../../modals/customerDetailAction/viewAllNotes';
import NoteModal from '../../../modals/orderDetails/addNewNote';
import { OrderNotesOptions } from '../../../order-details/constant';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../ui/customAccordian/customAccordian';
import { Table } from '../../../ui/customTable/Table';
import Loader from '../../../ui/loader';
import Pagination from '../../../utils/pagination';
import {
  CUSTOMER_NOTES_ITEMS_PER_PAGE,
  PAYLOAD_QUERY,
  RESPONSE_SUCCESS_MESSAGE,
  customerNoteOptions,
} from '../../constants';
import { notesTableHeaders } from '../../constants/tableFile';
import { FormattedNote } from '../../types';
import { transformNotes } from '../../utils';

const CustomerNotes = ({ Texts, addNewNotes }: { Texts: any; addNewNotes?: any }) => {
  const [customerNotes, setCustomerNotes] = useState<FormattedNote[]>([]);
  const [loading, setLoading] = useState(false);
  const { getCustomerNotes } = useAccount();
  const params = useSearchParams();
  const [value, setValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [allCustomerNotes, setAllCustomerNotes] = useState<FormattedNote[]>([]);

  const getNotes = async (pageNumber?: number, allPages?: boolean) => {
    setLoading(true);
    if (PAYLOAD_QUERY.OMS_CUSTOMER_ID && PAYLOAD_QUERY.CUSTOMER_ID && PAYLOAD_QUERY.DIVISION_CODE) {
      const payload = {
        divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
        customerId: params.get(PAYLOAD_QUERY.CUSTOMER_ID),
        omsCustomerId: params.get(PAYLOAD_QUERY.OMS_CUSTOMER_ID),
        pageNumber: allPages ? 1 : pageNumber ?? currentPage,
        pageSize: allPages ? 100 : CUSTOMER_NOTES_ITEMS_PER_PAGE,
      } as CustomerNotesPayload;
      await getCustomerNotes(payload)
        .then((response) => {
          if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
            const transformNoteToData = transformNotes(response?.Notes || []);
            if (!allPages) {
              setCustomerNotes(transformNoteToData);
              setTotalItems(response?.quantity ?? 0);
            } else {
              setAllCustomerNotes(transformNoteToData);
            }
          }
        })
        .catch(() => setLoading(false));
      setLoading(false);
    } else {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (value && customerNotes?.length === 0) {
      getNotes();
    }
  }, [value]);

  const onPageChange = async (page: number) => {
    setCurrentPage(page);
    await getNotes(page);
  };
  const showActiveBrandSection = useShowActiveBrandSection();

  return (
    <Accordion
      type="single"
      collapsible
      className="w-full rounded-md border border-foggyGray px-5 py-1 shadow-lg"
      onValueChange={setValue}
    >
      <AccordionItem value="openNote" className="border-none">
        <AccordionTrigger className="flex text-lg font-bold hover:no-underline">
          <p className="mr-auto">
            {Texts.customerNotes}
            {value && <>({totalItems ?? customerNotes?.length})</>}
          </p>
          {value && (
            <>
              <button
                onClick={(e) => e.preventDefault()}
                className="mr-2 h-[30px] rounded-md border-[1px] border-foggyGray px-4 py-1.5 text-xs font-medium"
              >
                <ViewAllNotesModals
                  customerNotes={allCustomerNotes}
                  noteOptions={OrderNotesOptions}
                  addNewNotes={addNewNotes}
                  isCustomerDetailsPage={true}
                  getNotes={getNotes}
                  setAllCustomerNotes={setAllCustomerNotes}
                  setCustomerNotes={setCustomerNotes}
                  setTotalItems={setTotalItems}
                  showActiveBrandSection={showActiveBrandSection}
                />
              </button>
              {showActiveBrandSection && (
                <button onClick={(e) => e.preventDefault()}>
                  <NoteModal
                    Texts={Texts}
                    noteOptions={customerNoteOptions}
                    setCustomerNotes={setCustomerNotes}
                    setAllCustomerNotes={setAllCustomerNotes}
                    setTotalItems={setTotalItems}
                    isViewAll={false}
                  />
                </button>
              )}
            </>
          )}
        </AccordionTrigger>
        <AccordionContent>
          {loading && <Loader />}
          <Table columns={notesTableHeaders} data={customerNotes?.slice(-5)} />
          {customerNotes && customerNotes?.length === 0 && <h1>{Texts.noCustomerNotes}</h1>}

          {totalItems > 5 && (
            <section className="mt-4 flex justify-end">
              <Pagination
                currentPage={currentPage}
                itemsPerPage={CUSTOMER_NOTES_ITEMS_PER_PAGE}
                totalItems={totalItems}
                onPageChange={onPageChange}
              ></Pagination>
            </section>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default CustomerNotes;
